import { useTreeStore } from '@/stores/tree'
import { useHeaderStore } from '@/stores/header'
import { useAccountsStore } from '@/stores/accounts'

import CountryFilter from './CountryFilter.vue'

export default CountryFilter

// Constants
export const AND = 'AND'
export const OR = 'OR'

// Constants for Prefills
export const DOMESTIC = 'Domestic'
export const INTERNATIONAL = 'International'
export const TRAVEL_BETWEEN = 'Travel between'
export const TRAVEL_BETWEEN_RETURN = 'Travel between (return)'

export const getPrefills = () => {
  // find the current user's client's base country
  const treeStore = useTreeStore()
  const headerStore = useHeaderStore()
  const accountsStore = useAccountsStore()

  const clients = treeStore.clients
  const countries = headerStore.countryData
  const userClient = accountsStore.user.active_client

  if (!clients || !userClient || !countries) return {}
  let baseCountry = countries.find(row => row.id === clients[userClient].base_country)
  baseCountry = baseCountry ? [baseCountry] : []
  return {
    [DOMESTIC]: [
      {criteria: START_IN, countries: baseCountry, condition: AND},
      {criteria: END_IN, countries: baseCountry, condition: AND},
    ],
    [INTERNATIONAL]: [
      {criteria: START_NOT, countries: baseCountry, condition: AND},
      {criteria: END_NOT, countries: baseCountry, condition: OR},
    ],
    [TRAVEL_BETWEEN]: [
      {criteria: START_IN, countries: [], condition: AND},
      {criteria: END_IN, countries: [], condition: AND},
    ],
    [TRAVEL_BETWEEN_RETURN]: [
      {criteria: START_IN, countries: [], condition: AND},
      {criteria: END_IN, countries: [], condition: AND},
      {criteria: END_IN, countries: [], condition: OR},
      {criteria: START_IN, countries: [], condition: AND},
    ],
  }
}

// Constants for CountryFilter
export const START_IN = 'start_in'
export const START_NOT = 'start_not_in'
export const END_IN = 'end_in'
export const END_NOT = 'end_not_in'
export const countryCriteriaOptions = [
  {value: START_IN, text: 'Origin in'},
  {value: START_NOT, text: 'Origin not in'},
  {value: END_IN, text: 'Destination in'},
  {value: END_NOT, text: 'Destination not in'},
]

const getCriteriaLabel = criteria => (criteria => criteria ? criteria.text : null)(
  countryCriteriaOptions.find(row => row.value == criteria)
)

// helpers for country filter
export const getDefaultRow = () => ({
  criteria: null,
  condition: AND,
  countries: [],
})

export const formatCountries = countries => {
  /*
    Formats a list of countries to (country.name, country.name, ...)
    If list is of single object, return it by itself
  */
  let names = countries.map(o => o.name);
  if (names.length > 1) names = ['(', names.join(', '), ')']
  return names.join('')
}

const formSentence = (filters) => {
  const result = []
  filters.forEach(({condition, criteria, countries}) => {
    if (result.length > 0) {
      result.push(condition == AND ? ', ' : ' OR ')
    }
    result.push(`${getCriteriaLabel(criteria)}: ${formatCountries(countries)}`)
  })
  return result
}

export const isPrefill = (filters) => {
  // figure out if the filters are a prefill
  const currentValue = formatForBackend(filters)
  const prefill = Object.entries(getPrefills())
    .find(([label, prefill]) => currentValue === formatForBackend(prefill) )
  if (prefill) return prefill[0]

  // or a travel between
  if (
    filters.length === 2 &&
    filters[0].criteria === START_IN &&
    filters[1].criteria === END_IN &&
    filters[1].condition === AND
  ) {
    return TRAVEL_BETWEEN
  }

  // or a travel between return
  if (
    filters.length === 4 &&
    [START_IN, END_IN, END_IN, START_IN].map((val, i) => val === filters[i].criteria).every(Boolean) &&
    [AND, AND, OR, AND].map((val, i) => val === filters[i].condition).every(Boolean) &&
    filters[0].countries.map((row, i) => row.id === filters[2].countries[i].id).every(Boolean) &&
    filters[1].countries.map((row, i) => row.id === filters[3].countries[i].id).every(Boolean)
  ) {
    return TRAVEL_BETWEEN_RETURN
  }
  return undefined
}

export const formatCountryFilters = filters => {
  /*
    Formats a list of country filters into human readable sentences
  */
  if (!filters) return ''

  const prefill = isPrefill(filters)
  if (prefill) {
    if ([TRAVEL_BETWEEN, TRAVEL_BETWEEN_RETURN].indexOf(prefill) !== -1) {
      return [
        prefill,': ',
        formatCountries(filters[0].countries), ' - ',
        formatCountries(filters[1].countries)
      ].join('')
    }
    return prefill
  }

  // otherwise form sentences based on each filter
  return formSentence(filters).join('')
}

export const formatForBackend = filters => {
  const result = []
  filters.forEach(({criteria, condition, countries}) => {
    result.push(`${condition}:${criteria}:${countries.map(c => c.id).join(',')}`)
  })
  return result.join(';')
}
